import { DateTime } from '@ksyos/date-time';
import { Map } from 'immutable';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { ManagerAuthorization } from '@common/enums';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { assertHasAuthorization } from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import type { IBreadcrumbItem } from '../../elements/common';
import type { ISubsequentService } from '../../interfaces/subsequentService';
import { RestEcgMeterSoftwareNames } from '../../../../common/enums/restEcgMeterSoftwareNames';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { ServiceType } from '../../../../common/enums/serviceType';
import { SpirometerSoftwareNames } from '../../../../common/enums/spirometerSoftwareNames';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { preferredServiceOrganizationProviderRemoveSchema } from '../../../../common/validation/preferredServiceOrganizationProviders';
import { serviceConsumerAddSchema, serviceConsumerEditSchema } from '../../../../common/validation/serviceConsumer';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { getServiceOrganizationSetting } from '../../../utils/apis/getServiceOrganizationSettings';
import { OverlapChecker } from '../../../utils/overlapChecker';
import { RateLimiter } from '../../../utils/rateLimiter';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    DateInput,
    Dropdown,
    EnumDropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCheckbox,
    InputGroup,
    Loading,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { CompensationPayoutList } from '../compensationPayouts/CompensationPayoutList';
import { ServiceConsumerAutoSubsequentServices } from './ServiceConsumerAutoSubsequentServices';
import { ServiceOrganizationProviderListAddEdit } from './ServiceOrganizationProviderListAddEdit';

type ServiceConsumerAddEditProps = IWithRouterProps;

export interface IAutoSubsequentService {
    id?: string;
    isDeviation: boolean;
    providerOrganizationId: string | null;
    providerPractitionerId: string | null;
    subsequentServiceId: string | null;
}

interface IEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: {
        emailAddressRequestInbox: string | null;
    };
    organizationId: string;
    practitionerEmailAddress: string | null;
}

interface IOrganization {
    emailAddressRequestInbox: string | null;
    id: string;
    name: string;
}

interface IPractitioner {
    emailAddress: string | null;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

export interface IService {
    code: ServiceCode;
    id: string;
    name: string;
    serviceType: ServiceType;
}

interface IPractitionerEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: IOrganization;
    organizationId: string;
}

interface IOrganizationEmployment {
    emailAddress: string | null;
    emailAddressAlt: string | null;
    id: string;
    organization: IOrganization;
    practitioner: IPractitioner;
    practitionerId: string;
}

export interface IServiceOrganizationProvider {
    id: string;
    isActive: boolean;
    name: string;
}

export type SelectedServiceOrganizationProviders = Map<string, IServiceOrganizationProvider>;

interface IPreferredServiceOrganizationProvider {
    id: string;
    organization: IServiceOrganizationProvider;
}

interface IServiceConsumer {
    autoSubsequentService: IAutoSubsequentService | null;
    compensationPayoutRequest: number | null;
    employmentId: string | null;
    endDate: string | null;
    followUpMeasurementGgz: boolean;
    isAutoCloseRequest: boolean;
    isGraderSelector: boolean;
    isRequestCreator: boolean;
    phoneNumberConsult: string | null;
    sendNotificationEmployment: boolean;
    sendNotificationEmploymentAlt: boolean;
    sendNotificationPractitioner: boolean;
    sendNotificationPractitionerWhenDeviant: boolean;
    sendZorgMailOnExaminationRequest: boolean;
    sendZorgMailOnReferralIntake: boolean;
    sendZorgMailOnReferralRequest: boolean;
    serviceId: string | null;
    spirometerSoftwareName: SpirometerSoftwareNames | null;
    startDate: string | null;
    usingRestEcgMeter: boolean;
    usingSpirometer: boolean;
}

interface IServiceConsumerAddEditState {
    employment?: IEmployment;

    errors: ValidationErrors;
    groupId?: string;
    isSaving: boolean;
    organizationEmployments: IOrganizationEmployment[];
    organizationId: string | null;

    organizations: IOrganization[];
    overlap?: {
        endDate: string;
        id: string;
        startDate: string;
    };

    practitioner?: IPractitioner;
    practitionerEmployments: IPractitionerEmployment[];
    preferredServiceProviderOrganizations: IPreferredServiceOrganizationProvider[];
    selectedServiceOrganizationProviders: SelectedServiceOrganizationProviders;
    service?: IService;
    serviceConsumer?: IServiceConsumer;
    serviceOrganizationSettingId: string | null;

    services: IService[];
    serviceType?: ServiceType;

    subsequentServices: ISubsequentService[];
}

class InnerServiceConsumerAddEdit extends Component<ServiceConsumerAddEditProps, IServiceConsumerAddEditState> {
    private atMostOncePerSecond = new RateLimiter(1000);
    private overlapChecker = new OverlapChecker('/api/serviceConsumers');
    private abortableFetch = new AbortableFetch();

    constructor(props: ServiceConsumerAddEditProps, context?: {}) {
        super(props, context);

        if (!this.getQueryPractitionerId() && !this.getQueryServiceId()) {
            throw new Error('No practitionerId or serviceId');
        } else if (this.getQueryPractitionerId() && this.getQueryServiceId()) {
            throw new Error('Cannot have both practitionerId and serviceId');
        }

        this.state = {
            services: [],
            organizations: [],
            practitionerEmployments: [],
            organizationEmployments: [],
            subsequentServices: [],
            isSaving: false,
            errors: emptyValidationErrors,
            organizationId: null,
            serviceOrganizationSettingId: null,

            preferredServiceProviderOrganizations: [],
            selectedServiceOrganizationProviders: Map<string, IServiceOrganizationProvider>(),
        };

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);
    }

    public async componentDidMount() {
        await this.setupComponent(this.props, this.getParamServiceConsumerId());
    }

    public render() {
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('serviceConsumer.serviceConsumers') }];
        if (this.isEdit()) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }
        if (this.getQueryPractitionerId() && this.state.practitioner) {
            breadcrumbs.push({
                text: getDisplayName(this.state.practitioner),
                url: `/practitioners/${this.getQueryPractitionerId()}`,
            });
        } else if (this.getQueryServiceId() && this.state.service) {
            breadcrumbs.push({
                text: this.state.service.name,
                url: `/services/${this.getQueryServiceId()}?tab=consumers`,
            });
        }

        const headerText =
            `${this.isEdit() ? translateKey('common.edit') : translateKey('common.new')}` +
            ` ${translateKey('serviceConsumer.serviceConsumer')} ${this.state.service ? this.state.service.name : ''}`;

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={headerText} />

                {this.getQueryPractitionerId() && (
                    <Loading
                        item={this.state.practitioner}
                        loaded={(practitioner) => (
                            <>{this.renderServiceConsumerAddEdit(practitioner, this.state.service)}</>
                        )}
                    />
                )}

                {this.getQueryServiceId() && (
                    <Loading
                        item={this.state.service}
                        loaded={(service) => <>{this.renderServiceConsumerAddEdit(this.state.practitioner, service)}</>}
                    />
                )}
            </FixedWidthTemplate>
        );
    }

    private renderServiceConsumerAddEdit(practitioner: IPractitioner | undefined, service: IService | undefined) {
        return (
            <Loading
                item={this.state.serviceConsumer}
                loaded={(serviceConsumerState) => (
                    <FormContainer onSubmit={async () => await this.save()}>
                        {this.state.overlap && (
                            <OverlapMessage
                                endDate={this.state.overlap.endDate}
                                link={
                                    `/serviceConsumers/${this.state.overlap.id}?` +
                                    `${
                                        this.getQueryServiceId() && serviceConsumerState.serviceId
                                            ? `serviceId=${this.getQueryServiceId()}`
                                            : ''
                                    }` +
                                    `${
                                        this.getQueryPractitionerId() && practitioner
                                            ? `practitionerId=${this.getQueryPractitionerId()}`
                                            : ''
                                    }` +
                                    `${
                                        this.getParamServiceConsumerId()
                                            ? `&previousId=${this.getParamServiceConsumerId()}`
                                            : ''
                                    }`
                                }
                                message={translateKey('serviceConsumer.overlapMessage')}
                                startDate={this.state.overlap.startDate}
                            />
                        )}

                        <FormGroup hasErrors={this.state.errors.has('startDate')}>
                            <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                            <InputGroup>
                                <DateInput
                                    error={this.state.errors.get('startDate')}
                                    onChange={async (value) => await this.handleChange('startDate', value)}
                                    value={serviceConsumerState.startDate}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={this.state.errors.has('endDate')}>
                            <FormLabel>{translateKey('common.endDate')}</FormLabel>
                            <InputGroup>
                                <DateInput
                                    error={this.state.errors.get('endDate')}
                                    onChange={async (value) => await this.handleChange('endDate', value)}
                                    value={serviceConsumerState.endDate}
                                />
                            </InputGroup>
                        </FormGroup>

                        {!this.isEdit() && this.getQueryPractitionerId() && (
                            <>
                                <FormHeader>
                                    {translateKey('employment.employment') + ' & ' + translateKey('service.service')}
                                </FormHeader>
                                <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                    <FormLabel isRequired>{translateKey('employment.employment')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('employmentId')}
                                            onChange={async (value) => {
                                                await this.handleChange('employmentId', value);
                                                await this.setState({ employment: undefined });
                                                await this.setEmploymentObject('practitioner');
                                                await this.checkOverlap();
                                                await this.loadPreferredServiceOrganizationProviders(
                                                    this.state.employment?.organizationId,
                                                    this.state.service?.id
                                                );
                                            }}
                                            onDoValidate={async () => await this.validateOne('employmentId')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('employment.employment')
                                            )}
                                            selectOptions={this.state.practitionerEmployments.map(
                                                (practitionerEmployment) => ({
                                                    value: practitionerEmployment.id,
                                                    text: practitionerEmployment.organization.name,
                                                })
                                            )}
                                            value={serviceConsumerState.employmentId}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                <FormGroup hasErrors={this.state.errors.has('serviceId')}>
                                    <FormLabel isRequired>{translateKey('service.service')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            error={this.state.errors.get('serviceId')}
                                            onChange={async (value) => {
                                                await this.handleChange('serviceId', value);
                                                let selectedService;
                                                if (value) {
                                                    selectedService = this.state.services.find(
                                                        (item) => item.id === value
                                                    );
                                                    await this.loadPreferredServiceOrganizationProviders(
                                                        this.state.employment?.organizationId,
                                                        value
                                                    );
                                                }
                                                await this.setState({ service: selectedService });
                                            }}
                                            onDoValidate={async () => await this.validateOne('serviceId')}
                                            placeholder={translateKey(
                                                'common.selectItem',
                                                translateKey('service.service')
                                            )}
                                            selectOptions={this.state.services.map((serviceItem) => ({
                                                value: serviceItem.id,
                                                text: serviceItem.name,
                                                optgroup: serviceItem.serviceType,
                                            }))}
                                            value={serviceConsumerState.serviceId}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </>
                        )}

                        {!this.isEdit() && this.getQueryServiceId() && (
                            <>
                                <FormHeader>
                                    {translateKey('organization.organization') +
                                        ' & ' +
                                        translateKey('practitioner.practitioner')}
                                </FormHeader>

                                <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                    <FormLabel isRequired>{translateKey('organization.organization')}</FormLabel>
                                    <InputGroup>
                                        <OrganizationSelection
                                            error={this.state.errors.get('employmentId')}
                                            isKsyosOrganizationExcluded={true}
                                            onChange={async (value) => {
                                                await this.setState({ organizationId: value || null });
                                                await this.loadOrganizationEmployments(value || null);
                                                await this.loadPreferredServiceOrganizationProviders(
                                                    value,
                                                    this.state.service?.id
                                                );
                                            }}
                                            value={this.state.organizationId ?? undefined}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {this.state.organizationId && (
                                    <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                        <FormLabel isRequired>{translateKey('employment.employment')}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                error={this.state.errors.get('employmentId')}
                                                onChange={async (value) => {
                                                    await this.handleChange('employmentId', value);
                                                    await this.setEmploymentObject('organization');
                                                    await this.checkOverlap();
                                                }}
                                                onDoValidate={async () => await this.validateOne('employmentId')}
                                                placeholder={translateKey(
                                                    'common.selectItem',
                                                    translateKey('employment.employment')
                                                )}
                                                selectOptions={this.state.organizationEmployments.map(
                                                    (organizationEmployment) => ({
                                                        value: organizationEmployment.id,
                                                        text: getDisplayName(organizationEmployment.practitioner),
                                                    })
                                                )}
                                                value={serviceConsumerState.employmentId}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        <FormHeader>{translateKey('setting.settings')}</FormHeader>

                        <FormGroup hasErrors={this.state.errors.has('isAutoCloseRequest')}>
                            <FormLabel>{translateKey('serviceConsumer.isAutoCloseRequest')}</FormLabel>
                            <InputGroup>
                                <InputCheckbox
                                    checked={serviceConsumerState.isAutoCloseRequest}
                                    error={this.state.errors.get('isAutoCloseRequest')}
                                    onChange={async (value) => await this.handleChange('isAutoCloseRequest', value)}
                                    onDoValidate={async () => await this.validateOne('isAutoCloseRequest')}
                                />
                            </InputGroup>
                        </FormGroup>

                        {service && service.serviceType === ServiceType.examination && (
                            <FormGroup hasErrors={this.state.errors.has('isGraderSelector')}>
                                <FormLabel>{translateKey('serviceConsumer.graderSelector')}</FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={serviceConsumerState.isGraderSelector}
                                        error={this.state.errors.get('isGraderSelector')}
                                        onChange={async (value) => await this.handleChange('isGraderSelector', value)}
                                        onDoValidate={async () => await this.validateOne('isGraderSelector')}
                                    />
                                </InputGroup>
                            </FormGroup>
                        )}

                        <FormGroup hasErrors={this.state.errors.has('isRequestCreator')}>
                            <FormLabel>
                                {this.state.service && this.state.service.serviceType === ServiceType.contact
                                    ? translateKey('serviceConsumer.createContact')
                                    : translateKey('serviceConsumer.createRequest')}
                            </FormLabel>
                            <InputGroup>
                                <InputCheckbox
                                    checked={serviceConsumerState.isRequestCreator}
                                    error={this.state.errors.get('isRequestCreator')}
                                    onChange={async (value) => {
                                        await this.handleChange('isRequestCreator', value);
                                        if (!value) {
                                            await this.setState(({ serviceConsumer }) => ({
                                                serviceConsumer: {
                                                    ...serviceConsumer,
                                                    sendZorgMailOnExaminationRequest: false,
                                                },
                                            }));
                                        }
                                    }}
                                    onDoValidate={async () => await this.validateOne('isRequestCreator')}
                                />
                            </InputGroup>
                        </FormGroup>

                        {this.props.router.params.id && (
                            <CompensationPayoutList
                                practitionerId={this.props.router.searchParams.getOptional('practitionerId')}
                                serviceConsumerId={this.props.router.params.id}
                                serviceId={this.props.router.searchParams.getOptional('serviceId')}
                            />
                        )}

                        {service && service.code === ServiceCode.TPC && (
                            <>
                                <FormGroup hasErrors={this.state.errors.has('usingSpirometer')}>
                                    <FormLabel>{translateKey('serviceConsumer.usingSpirometer')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceConsumerState.usingSpirometer}
                                            error={this.state.errors.get('usingSpirometer')}
                                            onChange={async (value) => {
                                                await this.handleChange('usingSpirometer', value);
                                                if (!value) {
                                                    await this.setState(({ serviceConsumer }) => ({
                                                        serviceConsumer: {
                                                            ...serviceConsumer,
                                                            spirometerSoftwareName: null,
                                                        },
                                                    }));
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('usingSpirometer')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {serviceConsumerState.usingSpirometer && (
                                    <FormGroup hasErrors={this.state.errors.has('spirometerSoftwareName')}>
                                        <FormLabel isRequired>
                                            {translateKey('serviceConsumer.spirometerSoftwareName')}
                                        </FormLabel>
                                        <InputGroup>
                                            <EnumDropdown
                                                enumName={'spirometerSoftwareNames'}
                                                error={this.state.errors.get('spirometerSoftwareName')}
                                                jsEnum={SpirometerSoftwareNames}
                                                onChange={async (value) =>
                                                    await this.handleChange('spirometerSoftwareName', value)
                                                }
                                                onDoValidate={async () =>
                                                    await this.validateOne('spirometerSoftwareName')
                                                }
                                                placeholder={translateKey('serviceConsumer.spirometerSoftwareName')}
                                                value={serviceConsumerState.spirometerSoftwareName}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {service && [ServiceCode.TCCi, ServiceCode.TCCh, ServiceCode.TCCr].includes(service.code) && (
                            <>
                                <FormGroup hasErrors={this.state.errors.has('usingRestEcgMeter')}>
                                    <FormLabel>{translateKey('serviceConsumer.usingRestEcgMeter')}</FormLabel>
                                    <InputGroup>
                                        <InputCheckbox
                                            checked={serviceConsumerState.usingRestEcgMeter}
                                            error={this.state.errors.get('usingRestEcgMeter')}
                                            onChange={async (value) => {
                                                await this.handleChange('usingRestEcgMeter', value);
                                                if (!value) {
                                                    await this.setState(({ serviceConsumer }) => ({
                                                        serviceConsumer: {
                                                            ...serviceConsumer,
                                                            restEcgMeterSoftwareName: null,
                                                        },
                                                    }));
                                                }
                                            }}
                                            onDoValidate={async () => await this.validateOne('usingRestEcgMeter')}
                                        />
                                    </InputGroup>
                                </FormGroup>

                                {serviceConsumerState.usingRestEcgMeter && (
                                    <FormGroup hasErrors={this.state.errors.has('restEcgMeterSoftwareName')}>
                                        <FormLabel isRequired>
                                            {translateKey('serviceConsumer.restEcgMeterSoftwareName')}
                                        </FormLabel>
                                        <InputGroup>
                                            <EnumDropdown
                                                enumName={'restEcgMeterSoftwareNames'}
                                                error={this.state.errors.get('restEcgMeterSoftwareName')}
                                                jsEnum={RestEcgMeterSoftwareNames}
                                                onChange={async (value) => Promise.resolve()}
                                                value={RestEcgMeterSoftwareNames.CardioPerfect}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}

                        {service && [ServiceCode.GGZOint, ServiceCode.GGZOembl].includes(service.code) && (
                            <FormGroup hasErrors={this.state.errors.has('followUpMeasurementGgz')}>
                                <FormLabel>{translateKey('serviceConsumer.sendFollowUpMeasurementGgz')}</FormLabel>
                                <InputGroup>
                                    <InputCheckbox
                                        checked={serviceConsumerState.followUpMeasurementGgz}
                                        error={this.state.errors.get('followUpMeasurementGgz')}
                                        onChange={async (value) => {
                                            await this.handleChange('followUpMeasurementGgz', value);
                                        }}
                                        onDoValidate={async () => await this.validateOne('followUpMeasurementGgz')}
                                    />
                                </InputGroup>
                            </FormGroup>
                        )}

                        <FormHeader>{translateKey('common.notifications')}</FormHeader>
                        {this.renderNotifications(serviceConsumerState, service, practitioner)}

                        {service && [ServiceType.examination, ServiceType.referral].includes(service.serviceType) && (
                            <>
                                {((service.serviceType === ServiceType.examination &&
                                    serviceConsumerState.isRequestCreator) ||
                                    service.serviceType === ServiceType.referral) && (
                                    <FormHeader>{translateKey('employment.zorgmail')}</FormHeader>
                                )}

                                {service.serviceType === ServiceType.examination &&
                                    serviceConsumerState.isRequestCreator && (
                                        <FormGroup
                                            hasErrors={this.state.errors.has('sendZorgMailOnExaminationRequest')}
                                        >
                                            <FormLabel>
                                                {translateKey('serviceConsumer.sendZorgMailOnExaminationRequest')}
                                            </FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceConsumerState.sendZorgMailOnExaminationRequest}
                                                    error={this.state.errors.get('sendZorgMailOnExaminationRequest')}
                                                    onChange={async (value) =>
                                                        await this.handleChange(
                                                            'sendZorgMailOnExaminationRequest',
                                                            value
                                                        )
                                                    }
                                                    onDoValidate={async () =>
                                                        await this.validateOne('sendZorgMailOnExaminationRequest')
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    )}

                                {service.serviceType === ServiceType.referral && (
                                    <>
                                        <FormGroup hasErrors={this.state.errors.has('sendZorgMailOnReferralRequest')}>
                                            <FormLabel>
                                                {translateKey('serviceConsumer.sendZorgMailOnReferralRequest')}
                                            </FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceConsumerState.sendZorgMailOnReferralRequest}
                                                    error={this.state.errors.get('sendZorgMailOnReferralRequest')}
                                                    onChange={async (value) =>
                                                        await this.handleChange('sendZorgMailOnReferralRequest', value)
                                                    }
                                                    onDoValidate={async () =>
                                                        await this.validateOne('sendZorgMailOnReferralRequest')
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                        <FormGroup hasErrors={this.state.errors.has('sendZorgMailOnReferralIntake')}>
                                            <FormLabel>
                                                {translateKey('serviceConsumer.sendZorgMailOnReferralIntake')}
                                            </FormLabel>
                                            <InputGroup>
                                                <InputCheckbox
                                                    checked={serviceConsumerState.sendZorgMailOnReferralIntake}
                                                    error={this.state.errors.get('sendZorgMailOnReferralIntake')}
                                                    onChange={async (value) =>
                                                        await this.handleChange('sendZorgMailOnReferralIntake', value)
                                                    }
                                                    onDoValidate={async () =>
                                                        await this.validateOne('sendZorgMailOnReferralIntake')
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </>
                                )}
                            </>
                        )}

                        {this.renderPreferredServiceOrganizationProviders(service)}

                        {this.renderServiceConsumerAutoSubsequentServices(serviceConsumerState, service)}

                        <CancelButton
                            disabled={this.state.isSaving}
                            to={this.getReturnUrl()}
                        />
                        <SaveSubmit disabled={!!this.state.overlap || this.state.isSaving} />
                    </FormContainer>
                )}
            />
        );
    }

    private renderNotifications(
        serviceConsumer: IServiceConsumer,
        service: IService | undefined,
        practitioner: IPractitioner | undefined
    ) {
        let practitionerMessage = '';
        if (practitioner) {
            if (practitioner.emailAddress) {
                practitionerMessage = practitioner.emailAddress;
            } else {
                practitionerMessage = translateKey('common.noEmailConfigured');
            }
        } else if (this.state.employment) {
            if (this.state.employment.practitionerEmailAddress) {
                practitionerMessage = this.state.employment.practitionerEmailAddress;
            } else {
                practitionerMessage = translateKey('common.noEmailConfigured');
            }
        }

        let employmentMessage = '';
        if (this.state.employment) {
            if (this.state.employment.emailAddress) {
                employmentMessage = this.state.employment.emailAddress;
            } else {
                employmentMessage = translateKey('common.noEmailConfigured');
            }
        } else {
            employmentMessage = translateKey('serviceConsumer.noEmploymentChosen');
        }

        let employmentAltMessage = '';
        if (this.state.employment) {
            if (this.state.employment.emailAddressAlt) {
                employmentAltMessage = this.state.employment.emailAddressAlt;
            } else {
                employmentAltMessage = translateKey('common.noEmailConfigured');
            }
        } else {
            employmentAltMessage = translateKey('serviceConsumer.noEmploymentChosen');
        }

        return (
            <>
                <FormGroup hasErrors={this.state.errors.has('sendNotificationPractitioner')}>
                    <FormLabel>{`${translateKey('serviceConsumer.sendNotificationPractitioner')} ${
                        practitionerMessage ? `(${practitionerMessage})` : ''
                    }`}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={serviceConsumer.sendNotificationPractitioner}
                            disabled={serviceConsumer.sendNotificationPractitionerWhenDeviant}
                            error={this.state.errors.get('sendNotificationPractitioner')}
                            onChange={async (value) => await this.handleChange('sendNotificationPractitioner', value)}
                            onDoValidate={async () => await this.validateOne('sendNotificationPractitioner')}
                        />
                    </InputGroup>
                </FormGroup>

                {service && service.serviceType === ServiceType.examination && (
                    <FormGroup hasErrors={this.state.errors.has('sendNotificationPractitionerWhenDeviant')}>
                        <FormLabel>{translateKey('serviceConsumer.sendNotificationPractitionerWhenDeviant')}</FormLabel>
                        <InputGroup>
                            <InputCheckbox
                                checked={serviceConsumer.sendNotificationPractitionerWhenDeviant}
                                disabled={serviceConsumer.sendNotificationPractitioner}
                                error={this.state.errors.get('sendNotificationPractitionerWhenDeviant')}
                                onChange={async (value) =>
                                    await this.handleChange('sendNotificationPractitionerWhenDeviant', value)
                                }
                                onDoValidate={async () =>
                                    await this.validateOne('sendNotificationPractitionerWhenDeviant')
                                }
                            />
                        </InputGroup>
                    </FormGroup>
                )}

                <FormGroup hasErrors={this.state.errors.has('sendNotificationEmployment')}>
                    <FormLabel>{`${translateKey(
                        'serviceConsumer.sendNotificationEmployment'
                    )} (${employmentMessage})`}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={serviceConsumer.sendNotificationEmployment}
                            error={this.state.errors.get('sendNotificationEmployment')}
                            onChange={async (value) => await this.handleChange('sendNotificationEmployment', value)}
                            onDoValidate={async () => await this.validateOne('sendNotificationEmployment')}
                        />
                    </InputGroup>
                </FormGroup>

                <FormGroup hasErrors={this.state.errors.has('sendNotificationEmploymentAlt')}>
                    <FormLabel>{`${translateKey(
                        'serviceConsumer.sendNotificationEmploymentAlt'
                    )} (${employmentAltMessage})`}</FormLabel>
                    <InputGroup>
                        <InputCheckbox
                            checked={serviceConsumer.sendNotificationEmploymentAlt}
                            error={this.state.errors.get('sendNotificationEmploymentAlt')}
                            onChange={async (value) => await this.handleChange('sendNotificationEmploymentAlt', value)}
                            onDoValidate={async () => await this.validateOne('sendNotificationEmploymentAlt')}
                        />
                    </InputGroup>
                </FormGroup>
            </>
        );
    }

    private renderPreferredServiceOrganizationProviders(service: IService | undefined) {
        if (
            service &&
            service.id &&
            (this.state.employment?.organizationId || this.state.organizationId) &&
            this.getServiceCode() !== ServiceCode.GGZOint &&
            this.getServiceCode() !== ServiceCode.GGZOembl &&
            this.state.selectedServiceOrganizationProviders
        ) {
            return (
                <ServiceOrganizationProviderListAddEdit
                    onChange={async (value: SelectedServiceOrganizationProviders) =>
                        await this.setState({ selectedServiceOrganizationProviders: value })
                    }
                    selectedServiceOrganizationProviders={this.state.selectedServiceOrganizationProviders}
                    service={service}
                    serviceOrganizationSettingId={this.state.serviceOrganizationSettingId}
                />
            );
        }
    }

    private renderServiceConsumerAutoSubsequentServices(
        serviceConsumerState: IServiceConsumer,
        service: IService | undefined
    ) {
        if (service && serviceConsumerState.employmentId && serviceConsumerState.autoSubsequentService) {
            return (
                <ServiceConsumerAutoSubsequentServices
                    autoSubsequentService={serviceConsumerState.autoSubsequentService}
                    employmentId={serviceConsumerState.employmentId}
                    errors={this.state.errors}
                    onChange={async (name, value) => {
                        if (name === 'subsequentServiceId') {
                            await this.setState(({ serviceConsumer }) => ({
                                serviceConsumer: {
                                    ...serviceConsumer,
                                    autoSubsequentService: {
                                        ...serviceConsumer!.autoSubsequentService,
                                        subsequentServiceId: value,
                                        providerOrganizationId: null,
                                        providerPractitionerId: null,
                                        isDeviation: false,
                                    },
                                },
                            }));
                        } else {
                            await this.setState(({ serviceConsumer }) => ({
                                serviceConsumer: {
                                    ...serviceConsumer,
                                    autoSubsequentService: { ...serviceConsumer!.autoSubsequentService, [name]: value },
                                },
                            }));
                        }
                    }}
                    onDoValidate={async (name) => await this.validateOneAutoSubsequentService(name)}
                    service={service}
                    serviceConsumerId={this.getParamServiceConsumerId()}
                />
            );
        }
    }

    private async validateOne(name: keyof IServiceConsumer) {
        await this.setState(({ serviceConsumer, errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), serviceConsumer, errors, name),
        }));
    }

    private async validateOneAutoSubsequentService(name: keyof IAutoSubsequentService) {
        if (name === 'subsequentServiceId') {
            this.isServiceConsumerForSubsequentService();
        }
        const autoSubsequentServiceName = 'autoSubsequentService.' + name;
        const serviceConsumer =
            this.state.serviceConsumer?.autoSubsequentService?.subsequentServiceId === null
                ? { ...this.state.serviceConsumer, autoSubsequentService: null }
                : this.state.serviceConsumer;
        await this.setState(({ errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), serviceConsumer, errors, autoSubsequentServiceName),
        }));
    }

    private async handleChange<K extends keyof IServiceConsumer>(name: K, value: IServiceConsumer[K]) {
        await this.setState(({ serviceConsumer }) => ({ serviceConsumer: { ...serviceConsumer, [name]: value } }));
        if (name === 'serviceId') {
            await this.setState(({ serviceConsumer }) => ({
                selectedServiceOrganizationProviders: Map(),
                serviceConsumer: {
                    ...serviceConsumer,
                    usingSpirometer: false,
                    spirometerSoftwareName: null,
                    usingRestEcgMeter: false,
                },
            }));
        }

        if (name === 'employmentId' || name === 'serviceId' || name === 'startDate' || name === 'endDate') {
            await this.checkOverlap();
        }
    }

    private async setupComponent(props: ServiceConsumerAddEditProps, serviceConsumerId: string | null) {
        const practitionerId = props.router.searchParams.get('practitionerId');
        const serviceId = props.router.searchParams.get('serviceId');
        if (serviceConsumerId) {
            const serviceConsumer = await this.getServiceConsumer(serviceConsumerId);
            this.setState(serviceConsumer);
            await this.loadPreferredServiceOrganizationProviders(
                serviceConsumer.employment.organizationId,
                serviceConsumer.service.id
            );

            await this.loadAutoSubsequentService(serviceConsumerId);
        } else {
            await this.setState({
                serviceConsumer: {
                    autoSubsequentService: {
                        providerOrganizationId: null,
                        providerPractitionerId: null,
                        isDeviation: false,
                        subsequentServiceId: null,
                    },
                    serviceId: this.getQueryServiceId(),
                    employmentId: null,
                    isAutoCloseRequest: false,
                    sendNotificationEmploymentAlt: false,
                    sendNotificationEmployment: false,
                    sendNotificationPractitioner: false,
                    sendNotificationPractitionerWhenDeviant: false,
                    sendZorgMailOnExaminationRequest: false,
                    sendZorgMailOnReferralIntake: false,
                    sendZorgMailOnReferralRequest: false,
                    isGraderSelector: false,
                    isRequestCreator: false,
                    compensationPayoutRequest: null,
                    startDate: DateTime.now().format('YYYY-MM-DD'),
                    endDate: null,
                    usingSpirometer: false,
                    spirometerSoftwareName: null,
                    usingRestEcgMeter: false,
                    followUpMeasurementGgz: false,
                    phoneNumberConsult: null,
                },
            });
            if (practitionerId) {
                await this.loadPractitioner(practitionerId);
                await this.loadPractitionerEmployments(practitionerId!);
                await this.loadAllServices();
            } else if (serviceId) {
                await this.loadAllOrganizations();
            }
        }

        if (practitionerId) {
            if (!this.state.practitioner) {
                await this.loadPractitioner(practitionerId);
            }
        } else if (serviceId) {
            await this.loadService(serviceId!);
        }

        await this.setState({ overlap: undefined });
    }

    private async checkOverlap() {
        const serviceId = this.getServiceId();
        if (serviceId) {
            this.atMostOncePerSecond.callMeMaybe(async () => {
                if (this.state.employment && this.state.serviceConsumer) {
                    const overlap = await this.overlapChecker.check(
                        { employmentId: this.state.employment.id, serviceId: serviceId },
                        this.state.serviceConsumer.startDate,
                        this.state.serviceConsumer.endDate,
                        this.getParamServiceConsumerId() || undefined
                    );
                    await this.setState({ overlap });
                }
            });
        }
    }

    private async loadPractitionerEmployments(practitionerId: string | null) {
        if (practitionerId) {
            const url = `/api/employments/${practitionerId}/organizations?isActive=true&isNoManager=true`;
            const results = await fetchJson(url, 'GET');
            if (results.json.items.length > 1) {
                return await this.setState({ practitionerEmployments: results.json.items });
            } else if (results.json.items.length === 1) {
                await this.setState(({ serviceConsumer }) => ({
                    serviceConsumer: { ...serviceConsumer, employmentId: results.json.items[0].id },
                    practitionerEmployments: results.json.items,
                }));

                return await this.setEmploymentObject('practitioner');
            }
        }

        await this.setState(({ serviceConsumer }) => ({
            serviceConsumer: { ...serviceConsumer, employmentId: null },
            practitionerEmployments: [],
            employment: null,
        }));
    }

    private async loadOrganizationEmployments(organizationId: string | null) {
        if (organizationId) {
            const url = `/api/employments/${organizationId}/practitioners?isActive=true&isNoManager=true`;
            const results = await fetchJson(url, 'GET');
            if (results.json.items.length === 1) {
                await this.setState(({ serviceConsumer }) => ({
                    serviceConsumer: { ...serviceConsumer, employmentId: results.json.items[0].id },
                    organizationEmployments: results.json.items,
                }));
                await this.setEmploymentObject('organization');

                return await this.checkOverlap();
            } else if (results.json.items.length > 1 || results.json.items.length === 0) {
                await this.setState(({ serviceConsumer }) => ({
                    serviceConsumer: { ...serviceConsumer, employmentId: undefined },
                    organizationEmployments: results.json.items,
                }));
            }
        }
    }

    private async setEmploymentObject(typeEmployment: 'practitioner' | 'organization') {
        if (this.state.serviceConsumer && this.state.serviceConsumer.employmentId) {
            let selectedEmployment;
            let organizationId;
            let practitionerEmailAddress;
            if (typeEmployment === 'practitioner' && this.state.practitioner) {
                selectedEmployment = this.state.practitionerEmployments.find(
                    (practitionerEmployment) => practitionerEmployment.id === this.state.serviceConsumer!.employmentId
                );
                organizationId = selectedEmployment && selectedEmployment.organizationId;
                practitionerEmailAddress = this.state.practitioner.emailAddress;
            } else if (typeEmployment === 'organization') {
                selectedEmployment = this.state.organizationEmployments.find(
                    (organizationEmployment) => organizationEmployment.id === this.state.serviceConsumer!.employmentId
                );
                organizationId = this.state.organizationId;
                practitionerEmailAddress = selectedEmployment && selectedEmployment.practitioner.emailAddress;
            }

            if (selectedEmployment && organizationId && practitionerEmailAddress) {
                const employment: IEmployment = {
                    id: this.state.serviceConsumer.employmentId,
                    emailAddress: selectedEmployment.emailAddress,
                    emailAddressAlt: selectedEmployment.emailAddressAlt,
                    organization: {
                        emailAddressRequestInbox: selectedEmployment.organization.emailAddressRequestInbox,
                    },
                    organizationId,
                    practitionerEmailAddress,
                };
                await this.setState({ employment });
                await this.validateOne('employmentId');
            }
        }
    }

    private async loadAutoSubsequentService(serviceConsumerId: string) {
        const url = `/api/autoSubsequentServices?serviceConsumerId=${serviceConsumerId}`;

        const result = await fetchJson(url, 'GET');
        if (result.json.items.length > 0) {
            await this.setState(({ serviceConsumer }) => ({
                serviceConsumer: {
                    ...serviceConsumer,
                    autoSubsequentService: result.json.items[0],
                },
            }));
        } else {
            await this.setState(({ serviceConsumer }) => ({
                serviceConsumer: {
                    ...serviceConsumer,
                    autoSubsequentService: {
                        providerOrganizationId: null,
                        providerPractitionerId: null,
                        isDeviation: false,
                        subsequentServiceId: null,
                    },
                },
            }));
        }
    }

    private async getServiceConsumer(serviceConsumerId: string) {
        const url = `/api/serviceConsumers/${serviceConsumerId}`;

        const result = await fetchJson(url, 'GET');
        const employment = {
            ...result.json.employment,
            practitionerEmailAddress: result.json.employment.practitioner?.emailAddress,
        };

        return {
            employment: employment,
            service: result.json.service,
            serviceConsumer: {
                ...result.json,
                serviceId: result.json.service.id,
                employmentId: result.json.employment.id,
            },
        };
    }

    private async loadPractitioner(practitionerId: string) {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ practitioner: result.json });
    }

    private async loadService(serviceId: string) {
        const url = `/api/services/${serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private async loadAllOrganizations() {
        const url = '/api/organizations/list';
        const results = await fetchJson(url, 'POST');
        if (results.json.items.length > 1) {
            await this.setState({ organizations: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState({ organizationId: results.json.items[0].id, organizations: results.json.items });
        }
    }

    private async loadPreferredServiceOrganizationProviders(organizationId?: string | null, serviceId?: string) {
        if (!organizationId || !serviceId) {
            await this.setState({
                serviceOrganizationSettingId: null,
                selectedServiceOrganizationProviders: Map<string, IServiceOrganizationProvider>(),
                preferredServiceProviderOrganizations: [],
            });

            return;
        }
        const serviceOrganizationSetting = await getServiceOrganizationSetting(organizationId, serviceId);
        if (serviceOrganizationSetting) {
            this.setState({ serviceOrganizationSettingId: serviceOrganizationSetting.id });
            const url = `/api/preferredServiceOrganizationProviders?serviceOrganizationSettingId=${serviceOrganizationSetting.id}`;
            const results = await fetchJson(url, 'GET');
            let selectedServiceOrganizationProviders: SelectedServiceOrganizationProviders = Map();
            results.json.items.forEach((item: IPreferredServiceOrganizationProvider) => {
                selectedServiceOrganizationProviders = selectedServiceOrganizationProviders.set(
                    item.organization.id,
                    item.organization
                );
            });
            await this.setState({
                selectedServiceOrganizationProviders,
                preferredServiceProviderOrganizations: results.json.items,
            });
        }
    }

    private async loadAllServices() {
        const url = '/api/services?selectionList=true&sort=serviceType';
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length > 1) {
            await this.setState({ services: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceConsumer }) => ({
                serviceConsumer: { ...serviceConsumer, serviceId: results.json.items[0].id },
                services: results.json.items,
            }));
        }
    }

    private getReturnUrl() {
        const previousId = this.props.router.searchParams.get('previousId');
        if (previousId) {
            return (
                `/serviceConsumers/${previousId}?` +
                `${
                    this.getQueryServiceId() && this.state.serviceConsumer && this.state.serviceConsumer.serviceId
                        ? `serviceId=${this.getQueryServiceId()}`
                        : ''
                }` +
                `${
                    this.getQueryPractitionerId() && this.state.practitioner
                        ? `practitionerId=${this.getQueryPractitionerId()}`
                        : ''
                }`
            );
        } else if (this.getQueryPractitionerId()) {
            return `/practitioners/${this.getQueryPractitionerId()}?tab=servicesConsumingListIsVisible`;
        } else if (this.getQueryServiceId()) {
            return `/services/${this.getQueryServiceId()}?tab=consumers`;
        }

        throw new Error('Unknown return destination');
    }

    private async save() {
        if (!this.state.serviceConsumer) {
            return;
        }

        const serviceConsumer = { ...this.state.serviceConsumer };
        if (serviceConsumer.autoSubsequentService && !serviceConsumer.autoSubsequentService.subsequentServiceId) {
            serviceConsumer.autoSubsequentService = null;
        }

        const validatedServiceConsumer = validate(this.getSchema(), serviceConsumer);
        let errors = validatedServiceConsumer.errors;
        const item = validatedServiceConsumer.item;

        const isMissingServiceConsumerRightsForSubsequentService =
            await this.isMissingServiceConsumerRightsForSubsequentService();
        if (isMissingServiceConsumerRightsForSubsequentService) {
            errors = errors.set('autoSubsequentService.subsequentServiceId', 'not_serviceConsumer_subsequent_service');
        }

        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        let url = '/api/serviceConsumers';

        if (this.isEdit()) {
            url += '/' + this.getParamServiceConsumerId();
        }

        await fetchJson(url, 'POST', item);

        this.state.selectedServiceOrganizationProviders
            .valueSeq()
            .forEach(async (selectedServiceOrganizationProvider) => {
                if (
                    selectedServiceOrganizationProvider &&
                    !this.state.preferredServiceProviderOrganizations.find(
                        (prefServiceOrgProv) =>
                            prefServiceOrgProv.organization.id === selectedServiceOrganizationProvider.id
                    )
                ) {
                    await fetchJson('/api/preferredServiceOrganizationProviders', 'POST', {
                        serviceOrganizationSettingId: this.state.serviceOrganizationSettingId,
                        organizationId: selectedServiceOrganizationProvider.id,
                    });
                }
            });
        for (const preferredServiceOrganizationProvider of this.state.preferredServiceProviderOrganizations) {
            if (
                !this.state.selectedServiceOrganizationProviders.has(
                    preferredServiceOrganizationProvider.organization.id
                )
            ) {
                const validationResult = validate(preferredServiceOrganizationProviderRemoveSchema, {
                    organizations: [preferredServiceOrganizationProvider.id],
                });

                await this.setState({ errors: validationResult.errors });
                if (validationResult.errors.size > 0) {
                    return;
                }
                await fetchJson('/api/preferredServiceOrganizationProviders', 'DELETE', validationResult.item);
            }
        }

        this.props.router.navigate(this.getReturnUrl());
    }

    private getSchema() {
        return this.isEdit() ? serviceConsumerEditSchema : serviceConsumerAddSchema;
    }

    private getQueryPractitionerId() {
        return this.props.router.searchParams.get('practitionerId');
    }

    private getQueryServiceId() {
        return this.props.router.searchParams.get('serviceId');
    }

    private getParamServiceConsumerId() {
        return this.props.router.params.id || null;
    }

    private isEdit() {
        return !!this.getParamServiceConsumerId();
    }

    private getServiceCode() {
        return this.state.service && this.state.service.code;
    }

    private getServiceId() {
        return this.state.serviceConsumer && this.state.serviceConsumer.serviceId;
    }

    private async isServiceConsumerForSubsequentService() {
        const isMissingServiceConsumerRightsForSubsequentService =
            await this.isMissingServiceConsumerRightsForSubsequentService();
        if (isMissingServiceConsumerRightsForSubsequentService) {
            this.setState({
                errors: this.state.errors.set(
                    'autoSubsequentService.subsequentServiceId',
                    'not_serviceConsumer_subsequent_service'
                ),
            });
        } else {
            const subsequentServiceIdError = this.state.errors.get('autoSubsequentService.subsequentServiceId');
            if (subsequentServiceIdError === 'not_serviceConsumer_subsequent_service') {
                this.setState({ errors: this.state.errors.remove('autoSubsequentService.subsequentServiceId') });
            }
        }
    }

    private async isMissingServiceConsumerRightsForSubsequentService() {
        if (!this.state.serviceConsumer?.autoSubsequentService?.subsequentServiceId) {
            return false;
        }

        const subsequentService = await this.getSubsequentService();
        if (!subsequentService) {
            return true;
        }

        const result = await this.abortableFetch.fetchJson(
            `/api/serviceConsumers?employmentId=${this.state.employment?.id}&isActive=true&serviceId=${subsequentService.succeedingServiceId}`,
            'GET'
        );
        const serviceConsumerList: IServiceConsumer[] = result.json.items;

        return !serviceConsumerList.length;
    }

    private async getSubsequentService() {
        const subsequentServices: ISubsequentService[] = (
            await this.abortableFetch.fetchJson(
                `/api/subsequentServices?serviceId=${this.getServiceId()}&employmentId=${this.state.employment?.id}`,
                'GET'
            )
        ).json.items;

        return subsequentServices.find(
            (subsequentservice) =>
                subsequentservice.id === this.state.serviceConsumer?.autoSubsequentService?.subsequentServiceId
        );
    }
}

export const ServiceConsumerAddEdit = withRouter(InnerServiceConsumerAddEdit);
